import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Activity, FileText, MessageSquare, BookOpen, Sparkles, ChevronRight, ExternalLink, Heart } from 'lucide-react';
import { useEffect } from 'react';

export default function Landing() {
  const navigate = useNavigate();
  const { isAuthenticated, loading } = useAuth();

  useEffect(() => {
    if (!loading && isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, loading, navigate]);

  const features = [
    { icon: Activity, title: "Track symptoms and patterns", description: "Record pain, energy, sleep, mood, and daily impact." },
    { icon: FileText, title: "Prepare for appointments", description: "Turn your recorded information into printable reports." },
    { icon: MessageSquare, title: "Build confidence in conversations", description: "Practice asking for support, setting boundaries, and discussing accommodations." },
    { icon: BookOpen, title: "Reflect and develop skills", description: "Use your journal and guided activities to explore what helps." },
    { icon: Sparkles, title: "Find words that work for you", description: "Access scripts and disclosure tools when you choose to share." }
  ];

  return (
    <div className="min-h-screen pb-safe">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center opacity-5" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1689600549574-d688a1e5a731?crop=entropy&cs=srgb&fm=jpg&q=85')" }} />
        
        <div className="relative max-w-lg mx-auto px-6 pt-16 pb-12">
          <div className="page-enter">
            <h1 className="font-heading text-4xl sm:text-5xl font-light text-foreground leading-tight">
              PwSD<br /><span className="font-semibold">Navigator</span>
            </h1>
            
            <p className="mt-6 text-lg font-medium text-foreground leading-relaxed">
              Understand your patterns. Express your needs. Navigate your day.
            </p>
            
            <p className="mt-4 text-muted-foreground leading-relaxed">
              Living with an invisible or dynamic disability can mean changing symptoms, shifting energy, and needs that are difficult to explain. PwSD Navigator gives you tools to track your experiences, prepare for conversations, and find support that fits your life.
            </p>
            
            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <Button data-testid="get-started-btn" onClick={() => navigate('/auth')} className="btn-primary text-lg">
                Get Started<ChevronRight className="ml-2 h-5 w-5" />
              </Button>
              <Button data-testid="learn-more-btn" variant="ghost" onClick={() => navigate('/about')} className="text-primary hover:bg-secondary">
                Learn More
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Access Cards */}
      <div className="max-w-lg mx-auto px-6 py-6">
        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={() => navigate('/conditions')}
            data-testid="landing-conditions"
            className="card-soft bg-purple-50 border-purple-200 hover:border-purple-400 p-4 text-left"
          >
            <BookOpen className="h-6 w-6 text-purple-600 mb-2" />
            <h3 className="font-heading font-semibold text-foreground">Conditions Guide</h3>
            <p className="text-xs text-muted-foreground mt-1">Explore common conditions with disclosure tips</p>
          </button>
          
          <button
            onClick={() => navigate('/resources')}
            data-testid="landing-resources"
            className="card-soft bg-teal-50 border-teal-200 hover:border-teal-400 p-4 text-left"
          >
            <Heart className="h-6 w-6 text-teal-600 mb-2" />
            <h3 className="font-heading font-semibold text-foreground">Resources</h3>
            <p className="text-xs text-muted-foreground mt-1">Programs & support from PwSD</p>
          </button>
        </div>
      </div>

      {/* Features Section */}
      <div className="max-w-lg mx-auto px-6 py-8">
        <h2 className="font-heading text-2xl font-semibold text-foreground mb-6 page-enter">Support for everyday life</h2>
        
        <div className="space-y-4">
          {features.map((feature, index) => (
            <div key={feature.title} className={`card-soft page-enter stagger-${Math.min(index + 1, 4)}`}>
              <div className="flex items-start gap-4">
                <div className="p-3 bg-secondary rounded-2xl">
                  <feature.icon className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-heading text-lg font-semibold text-foreground">{feature.title}</h3>
                  <p className="mt-1 text-muted-foreground">{feature.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Understanding Silent Disabilities */}
      <div className="max-w-lg mx-auto px-6 py-6">
        <div className="card-soft page-enter stagger-4">
          <h2 className="font-heading text-xl font-semibold text-foreground mb-3">Understanding Silent Disabilities</h2>
          <p className="text-muted-foreground leading-relaxed">
            Silent or invisible disabilities encompass conditions like cognitive disorders, autoimmune diseases, and mental health issues. Though not physically apparent, they profoundly affect daily life.
          </p>
          <button onClick={() => navigate('/conditions')} className="mt-4 flex items-center gap-2 text-primary font-medium hover:underline">
            <span>Explore all conditions</span><ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* PwSD Association Credit */}
      <div className="max-w-lg mx-auto px-6 py-4">
        <div className="card-soft bg-primary/5 border-primary/20 page-enter stagger-4">
          <p className="text-sm text-muted-foreground">
            <strong className="text-foreground">Powered by insights from</strong> the Persons with Silent Disabilities Association—a 501(c)(3) nonprofit.
          </p>
          <a href="https://silent-disabilities.org" target="_blank" rel="noopener noreferrer" className="mt-3 inline-flex items-center gap-2 text-primary text-sm hover:underline">
            <ExternalLink className="h-4 w-4" />Learn more at silent-disabilities.org
          </a>
        </div>
      </div>

      {/* Trust Section */}
      <div className="max-w-lg mx-auto px-6 py-6">
        <div className="card-soft bg-secondary/50 text-center page-enter stagger-4">
          <p className="text-sm text-muted-foreground">Built with care by the disability community, for the disability community.</p>
        </div>
      </div>
    </div>
  );
}
